import { useState, useEffect, useMemo } from 'react';
import { getBacktestScans, getBacktestScansRange, runBacktest, deleteScan, getSimulatedResults, getSimulatedResultsRange } from './services/apiService';
import { getETNow, getETDateString } from './utils/marketUtils';
import { isActualEntry } from './utils/tradeUtils';
import { AI_SOURCE } from './constants/ai';
import './BacktestDialog.css';

// Entry cutoff: scans after this time are marked "Next day" (configurable)
const ENTRY_CUTOFF_HOUR = 12; // 12:00 PM ET

// Check if scan was made after entry cutoff (scan timestamps are stored as ET strings)
const isScanAfterCutoff = (timestamp) => {
  if (!timestamp) return false;
  const match = String(timestamp).match(/T(\d{2}):/);
  return match ? parseInt(match[1], 10) >= ENTRY_CUTOFF_HOUR : false;
};

const BacktestDialog = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('scan-manager');
  const [scans, setScans] = useState([]);
  const [selectedScanIds, setSelectedScanIds] = useState(new Set());
  const [forceMode, setForceMode] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [runSummary, setRunSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);

  const today = useMemo(() => getETDateString(), []);

  // Scan Manager date range state
  const [scanTimeRange, setScanTimeRange] = useState('today');
  const [scanCustomStart, setScanCustomStart] = useState(today);
  const [scanCustomEnd, setScanCustomEnd] = useState(today);

  // Analysis tab state
  const [timeRange, setTimeRange] = useState('today');
  const [analysisCustomStart, setAnalysisCustomStart] = useState(today);
  const [analysisCustomEnd, setAnalysisCustomEnd] = useState(today);
  const [sortColumn, setSortColumn] = useState('profit');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ source: 'all', reason: 'all', ai: 'all', result: 'all' });

  const PAGE_SIZE = 20;

  // Load scans on mount
  useEffect(() => {
    loadScans();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load analysis trades on mount
  useEffect(() => {
    loadTrades('today');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScans = async (range = scanTimeRange, clearSummary = true) => {
    try {
      let result;
      if (range === 'today') {
        result = await getBacktestScans(today);
      } else if (range === 'custom') {
        result = await getBacktestScansRange(scanCustomStart, scanCustomEnd);
      } else {
        const d = getETNow();
        if (range === 'week') d.setDate(d.getDate() - 7);
        else if (range === 'month') d.setDate(d.getDate() - 30);
        const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        result = await getBacktestScansRange(start, today);
      }
      const allScans = result || [];
      setScans(allScans);
      // Auto-select new scans (not backtested, not after-cutoff)
      const newIds = new Set();
      allScans.forEach(s => {
        if (!s.backtested && !isScanAfterCutoff(s.timestamp)) newIds.add(s.id);
      });
      setSelectedScanIds(newIds);
      if (clearSummary) setRunSummary(null);
    } catch (err) {
      console.error('Failed to load scans:', err);
    }
  };

  const handleScanTimeRangeChange = (range) => {
    setScanTimeRange(range);
    loadScans(range);
  };

  const loadTrades = async (range) => {
    try {
      let result;
      if (range === 'today') {
        result = await getSimulatedResults(today);
      } else if (range === 'custom') {
        result = await getSimulatedResultsRange(analysisCustomStart, analysisCustomEnd);
      } else {
        const end = today;
        const d = getETNow();
        if (range === 'week') {
          d.setDate(d.getDate() - 7);
        } else if (range === 'month') {
          d.setDate(d.getDate() - 30);
        } else {
          d.setDate(d.getDate() - 365);
        }
        const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        result = await getSimulatedResultsRange(start, end);
      }

      // Parse trades from results
      const allTrades = [];
      (result || []).forEach(r => {
        try {
          const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
          allTrades.push(...(data.trades || []));
        } catch {}
      });
      setTrades(allTrades);
      setStats(calculateStats(allTrades));
      setPage(0);
    } catch (err) {
      console.error('Failed to load trades:', err);
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    loadTrades(range);
  };

  const handleToggleScan = (id) => {
    setSelectedScanIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectableIds = scans
      .filter(s => forceMode || (!s.backtested && !isScanAfterCutoff(s.timestamp)))
      .map(s => s.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedScanIds.has(id));
    if (allSelected) {
      setSelectedScanIds(new Set());
    } else {
      setSelectedScanIds(new Set(selectableIds));
    }
  };

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Delete this scan and associated backtest data?')) return;
    try {
      await deleteScan(scanId);
      selectedScanIds.delete(scanId);
      setSelectedScanIds(new Set(selectedScanIds));
      await loadScans(scanTimeRange);
      await loadTrades(timeRange);
    } catch (err) {
      console.error('Failed to delete scan:', err);
    }
  };

  const handleRunBacktest = async () => {
    setRunning(true);
    setRunSummary(null);
    setProgress({ text: 'Running backtest...', percent: 0 });
    try {
      // Group selected scans by date
      const scansByDate = {};
      scans.forEach(s => {
        if (!selectedScanIds.has(s.id)) return;
        const date = String(s.timestamp).substring(0, 10);
        if (!scansByDate[date]) scansByDate[date] = [];
        scansByDate[date].push(s.id);
      });

      const dates = Object.keys(scansByDate).sort();
      const allResultTrades = [];

      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        setProgress({ text: `Running ${date}...`, percent: Math.round(((i + 1) / dates.length) * 90) });
        const result = await runBacktest(date, scansByDate[date], forceMode);
        allResultTrades.push(...(result.trades || []));
      }

      setProgress({ text: 'Complete!', percent: 100 });

      // Build summary from this run's results (exclude skipped trades)
      const entered = allResultTrades.filter(isActualEntry);
      const wins = entered.filter(t => t.profit > 0);
      const totalPL = entered.reduce((s, t) => s + (t.profit || 0), 0);
      const dedupTrades = allResultTrades.filter(t => t.exitReason === 'SKIPPED_BY_DEDUP');
      const skipTrades = allResultTrades.filter(t => t.exitReason === 'SKIPPED_BY_RISK');
      const dedupSaved = -dedupTrades.reduce((s, t) => s + (t.simProfit || 0), 0);
      const skipSaved = -skipTrades.reduce((s, t) => s + (t.simProfit || 0), 0);
      setRunSummary({
        picks: allResultTrades.length,
        entered: entered.length,
        wins: wins.length,
        losses: entered.length - wins.length,
        totalPL,
        skippedDedup: dedupTrades.length,
        skippedRisk: skipTrades.length,
        dedupSaved,
        skipSaved,
      });

      // Reload all trades from DB (merges all scans)
      await loadTrades(timeRange);
      await loadScans(scanTimeRange, false);
      setTimeout(() => setProgress(null), 2000);
    } catch (err) {
      setProgress({ text: `Error: ${err.message}`, percent: 0 });
      setRunSummary(null);
    } finally {
      setRunning(false);
    }
  };

  // ==========================================
  // Stats Calculation (client-side for display)
  // ==========================================

  // isActualEntry imported from utils/tradeUtils

  const calculateStats = (trades) => {
    const entered = trades.filter(isActualEntry);
    const wins = entered.filter(t => t.profit > 0);
    const losses = entered.filter(t => t.profit <= 0);
    const totalProfit = entered.reduce((s, t) => s + (t.profit || 0), 0);

    return {
      totalTrades: trades.length,
      enteredTrades: entered.length,
      wins: wins.length,
      losses: losses.length,
      winRate: entered.length > 0 ? ((wins.length / entered.length) * 100).toFixed(1) : '0.0',
      totalProfit: totalProfit.toFixed(2),
      avgProfit: entered.length > 0 ? (totalProfit / entered.length).toFixed(2) : '0.00',
      exitReasons: {
        target: trades.filter(t => t.exitReason === 'target').length,
        stop_loss: trades.filter(t => t.exitReason === 'stop_loss').length,
        time_cutoff: trades.filter(t => t.exitReason === 'time_cutoff').length,
        no_entry: trades.filter(t => t.exitReason === 'no_entry').length,
        no_data: trades.filter(t => t.exitReason === 'no_data').length,
        skipped_dedup: trades.filter(t => t.exitReason === 'SKIPPED_BY_DEDUP').length,
        skipped_risk: trades.filter(t => t.exitReason === 'SKIPPED_BY_RISK').length,
      },
      byAI: {
        claude: calcSubset(trades.filter(t => t.claudeRecommended)),
        gpt: calcSubset(trades.filter(t => t.gptRecommended)),
        both: calcSubset(trades.filter(t => t.source === AI_SOURCE.BOTH)),
      },
    };
  };

  const calcSubset = (trades) => {
    const entered = trades.filter(isActualEntry);
    const wins = entered.filter(t => t.profit > 0);
    return {
      total: trades.length,
      entered: entered.length,
      wins: wins.length,
      winRate: entered.length > 0 ? ((wins.length / entered.length) * 100).toFixed(1) : '0.0',
      totalProfit: entered.reduce((s, t) => s + (t.profit || 0), 0).toFixed(2),
    };
  };

  // ==========================================
  // Filtering & Sorting
  // ==========================================

  const filteredTrades = useMemo(() => {
    return trades.filter(t => {
      if (filters.source !== 'all' && t.source !== filters.source) return false;
      if (filters.reason !== 'all' && t.exitReason !== filters.reason) return false;
      if (filters.ai === AI_SOURCE.CLAUDE && !t.claudeRecommended) return false;
      if (filters.ai === AI_SOURCE.GPT && !t.gptRecommended) return false;
      if (filters.ai === AI_SOURCE.BOTH && t.source !== AI_SOURCE.BOTH) return false;
      if (filters.result === 'win' && (t.profit || 0) <= 0) return false;
      if (filters.result === 'loss' && (t.profit || 0) > 0) return false;
      return true;
    });
  }, [trades, filters]);

  const sortedTrades = useMemo(() => {
    const sorted = [...filteredTrades];
    sorted.sort((a, b) => {
      let va, vb;
      switch (sortColumn) {
        case 'symbol': va = a.symbol || ''; vb = b.symbol || ''; break;
        case 'source': va = a.source || ''; vb = b.source || ''; break;
        case 'entry': va = a.entryPrice || 0; vb = b.entryPrice || 0; break;
        case 'exit': va = a.exitPrice || 0; vb = b.exitPrice || 0; break;
        case 'profit': va = a.profit || 0; vb = b.profit || 0; break;
        case 'reason': va = a.exitReason || ''; vb = b.exitReason || ''; break;
        case 'duration': va = a.duration || 0; vb = b.duration || 0; break;
        case 'scan': va = a.scanTime || ''; vb = b.scanTime || ''; break;
        default: va = a.profit || 0; vb = b.profit || 0;
      }
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredTrades, sortColumn, sortDirection]);

  const pagedTrades = useMemo(() => {
    return sortedTrades.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [sortedTrades, page]);

  const totalPages = Math.max(1, Math.ceil(sortedTrades.length / PAGE_SIZE));

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const bestTrades = useMemo(() =>
    [...trades].filter(t => t.profit > 0).sort((a, b) => b.profit - a.profit).slice(0, 5),
  [trades]);

  const worstTrades = useMemo(() =>
    [...trades].filter(t => t.profit < 0).sort((a, b) => a.profit - b.profit).slice(0, 5),
  [trades]);

  const bySource = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const src = t.source || 'unknown';
      if (!map[src]) map[src] = [];
      map[src].push(t);
    });
    return Object.entries(map).map(([source, group]) => ({
      source,
      ...calcSubset(group),
    }));
  }, [trades]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================
  // Render
  // ==========================================

  return (
    <div className="backtest-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="backtest-dialog">
        <div className="backtest-header">
          <h2>Backtest Manager</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="backtest-tabs">
          <button
            className={`backtest-tab ${activeTab === 'scan-manager' ? 'active' : ''}`}
            onClick={() => setActiveTab('scan-manager')}
          >Scan Manager</button>
          <button
            className={`backtest-tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >Analysis</button>
        </div>

        <div className="backtest-body">
          {activeTab === 'scan-manager' ? renderScanManager() : renderAnalysis()}
        </div>
      </div>
    </div>
  );

  function renderScanManager() {
    const selectedCount = selectedScanIds.size;

    // Group scans by date
    const scansByDate = {};
    scans.forEach(scan => {
      const date = String(scan.timestamp).substring(0, 10);
      if (!scansByDate[date]) scansByDate[date] = [];
      scansByDate[date].push(scan);
    });
    const sortedDates = Object.keys(scansByDate).sort().reverse();

    return (
      <>
        <div className="analysis-timerange" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {[['today', 'Today'], ['week', '7 Days'], ['month', '30 Days'], ['custom', 'Custom']].map(([r, label]) => (
            <button key={r} className={scanTimeRange === r ? 'active' : ''} onClick={() => handleScanTimeRangeChange(r)}>
              {label}
            </button>
          ))}
          {scanTimeRange === 'custom' && (
            <>
              <input type="date" value={scanCustomStart} max={today}
                style={{ fontSize: 14, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                onChange={e => setScanCustomStart(e.target.value)} />
              <span style={{ fontSize: 14, color: '#888', lineHeight: '32px' }}>to</span>
              <input type="date" value={scanCustomEnd} max={today}
                style={{ fontSize: 14, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                onChange={e => setScanCustomEnd(e.target.value)} />
              <button style={{ fontSize: 14, padding: '6px 12px' }}
                onClick={() => loadScans('custom')}>Go</button>
            </>
          )}
          <span style={{ fontSize: 13, color: '#888', marginLeft: 8, lineHeight: '32px' }}>
            {scans.length} scan(s)
          </span>
        </div>

        <div className="scan-list">
          {scans.length === 0 && (
            <div className="no-data">No scans found</div>
          )}
          {scans.length > 0 && (
            <div className="scan-item" style={{ borderBottom: '2px solid #ddd', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={(() => {
                  const selectableIds = scans
                    .filter(s => forceMode || (!s.backtested && !isScanAfterCutoff(s.timestamp)))
                    .map(s => s.id);
                  return selectableIds.length > 0 && selectableIds.every(id => selectedScanIds.has(id));
                })()}
                onChange={handleSelectAll}
              />
              <span style={{ fontSize: 13 }}>Select All</span>
            </div>
          )}
          {sortedDates.map(date => (
            <div key={date}>
              {scanTimeRange !== 'today' && (
                <div style={{ fontSize: 14, fontWeight: 600, color: '#667eea', padding: '10px 0 6px', borderBottom: '1px solid #eee' }}>
                  {date} · {scansByDate[date].length} scan(s)
                </div>
              )}
              {scansByDate[date].map(scan => {
                const ts = new Date(scan.timestamp);
                const time = ts.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                const isAfterCutoff = isScanAfterCutoff(scan.timestamp);

                let pickCount = 0;
                try {
                  const data = typeof scan.data === 'string' ? JSON.parse(scan.data) : scan.data;
                  const c = data?.claudeResult?.topPicks?.length || 0;
                  const g = data?.openaiResult?.topPicks?.length || 0;
                  pickCount = c + g;
                } catch {}

                const statusClass = scan.backtested ? 'done' : isAfterCutoff ? 'next-day' : 'new';
                const statusLabel = scan.backtested ? 'Backtested' : isAfterCutoff ? 'Next day' : 'New';

                return (
                  <div key={scan.id} className={`scan-item ${scan.backtested ? 'backtested' : ''} ${forceMode ? 'force-mode' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedScanIds.has(scan.id)}
                      onChange={() => handleToggleScan(scan.id)}
                      disabled={(scan.backtested && !forceMode) || isAfterCutoff}
                    />
                    <span className="scan-time">{time}</span>
                    <span className="scan-source">{scan.source === 'auto' ? 'Auto' : 'Manual'}</span>
                    <span className="scan-picks">{pickCount} picks</span>
                    <span className={`scan-status ${statusClass}`}>
                      {statusLabel}
                    </span>
                    <button className="delete-scan-btn" onClick={(e) => { e.stopPropagation(); handleDeleteScan(scan.id); }} title="Delete">🗑️</button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="scan-actions">
          <button
            className="run-btn"
            onClick={handleRunBacktest}
            disabled={running || selectedCount === 0}
          >
            {running ? 'Running...' : `Run Selected (${selectedCount})`}
          </button>
          <label>
            <input type="checkbox" checked={forceMode} onChange={e => setForceMode(e.target.checked)} />
            Force re-run
          </label>
        </div>

        {progress && (
          <div className="progress-bar">
            <div className="progress-text">{progress.text}</div>
            <div className="bar">
              <div className="bar-fill" style={{ width: `${progress.percent}%` }} />
            </div>
          </div>
        )}

        {runSummary && !running && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: runSummary.totalPL >= 0 ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${runSummary.totalPL >= 0 ? '#bbf7d0' : '#fecaca'}`,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span>{runSummary.totalPL >= 0 ? '✅' : '⚠️'}</span>
            <span>
              Backtest complete: <strong>{runSummary.entered}/{runSummary.picks} entered</strong>
              {runSummary.entered > 0 && (
                <>, {runSummary.wins}W / {runSummary.losses}L, P&amp;L: <span style={{
                  fontWeight: 600,
                  color: runSummary.totalPL >= 0 ? '#16a34a' : '#dc2626',
                }}>{runSummary.totalPL >= 0 ? '+' : ''}${runSummary.totalPL.toFixed(2)}</span></>
              )}
              {(runSummary.skippedDedup > 0 || runSummary.skippedRisk > 0) && (
                <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>
                  (dedup: {runSummary.skippedDedup || 0}, saved: <span style={{ color: (runSummary.dedupSaved || 0) >= 0 ? '#16a34a' : '#dc2626' }}>${(runSummary.dedupSaved || 0).toFixed(0)}</span>
                  {' | '}skip: {runSummary.skippedRisk || 0}, saved: <span style={{ color: (runSummary.skipSaved || 0) >= 0 ? '#16a34a' : '#dc2626' }}>${(runSummary.skipSaved || 0).toFixed(0)}</span>)
                </span>
              )}
              {runSummary.entered === 0 && runSummary.picks > 0 && <>, no entries triggered</>}
            </span>
          </div>
        )}
      </>
    );
  }

  function renderAnalysis() {
    return (
      <>
        <div className="analysis-timerange" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {[['today', 'Today'], ['week', '7 Days'], ['month', '30 Days'], ['all', 'All'], ['custom', 'Custom']].map(([r, label]) => (
            <button key={r} className={timeRange === r ? 'active' : ''} onClick={() => handleTimeRangeChange(r)}>
              {label}
            </button>
          ))}
          {timeRange === 'custom' && (
            <>
              <input type="date" value={analysisCustomStart} max={today}
                style={{ fontSize: 14, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                onChange={e => setAnalysisCustomStart(e.target.value)} />
              <span style={{ fontSize: 14, color: '#888', lineHeight: '32px' }}>to</span>
              <input type="date" value={analysisCustomEnd} max={today}
                style={{ fontSize: 14, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                onChange={e => setAnalysisCustomEnd(e.target.value)} />
              <button style={{ fontSize: 14, padding: '6px 12px' }}
                onClick={() => loadTrades('custom')}>Go</button>
            </>
          )}
        </div>

        {!stats || trades.length === 0 ? (
          <div className="no-data">No simulated trades found for this period</div>
        ) : (
          <>
            {/* Summary - Hero metrics */}
            <div className="stats-card summary-hero">
              <div className="hero-row">
                <div className="hero-metric">
                  <span className="hero-value">{stats.winRate}%</span>
                  <span className="hero-label">Win Rate</span>
                </div>
                <div className="hero-metric">
                  <span className={`hero-value ${parseFloat(stats.totalProfit) > 0 ? 'positive' : parseFloat(stats.totalProfit) < 0 ? 'negative' : ''}`}>${stats.totalProfit}</span>
                  <span className="hero-label">P&L</span>
                </div>
                <div className="hero-metric">
                  <span className="hero-value">{stats.enteredTrades}/{stats.totalTrades}</span>
                  <span className="hero-label">Trades</span>
                </div>
                <div className="hero-metric">
                  <span className={`hero-value ${parseFloat(stats.avgProfit) > 0 ? 'positive' : parseFloat(stats.avgProfit) < 0 ? 'negative' : ''}`}>${stats.avgProfit}</span>
                  <span className="hero-label">Avg</span>
                </div>
              </div>
              <div className="summary-details">
                <span>Target: {stats.exitReasons.target}</span>
                <span>Stop Loss: {stats.exitReasons.stop_loss}</span>
                <span>Cutoff: {stats.exitReasons.time_cutoff}</span>
                <span>No Entry: {stats.exitReasons.no_entry}</span>
                {(stats.exitReasons.skipped_dedup > 0 || stats.exitReasons.skipped_risk > 0) && <>
                  <span style={{ color: '#888' }}>Dedup: {stats.exitReasons.skipped_dedup || 0}</span>
                  <span style={{ color: '#888' }}>Skip: {stats.exitReasons.skipped_risk || 0}</span>
                </>}
              </div>
              {(stats.exitReasons.skipped_dedup > 0 || stats.exitReasons.skipped_risk > 0) && (
                <div className="summary-details" style={{ fontSize: 12 }}>
                  {stats.exitReasons.skipped_dedup > 0 && <span>Dedup saved: <span style={{ color: (-trades.filter(t => t.exitReason === 'SKIPPED_BY_DEDUP').reduce((s, t) => s + (t.simProfit || 0), 0)) >= 0 ? '#16a34a' : '#dc2626' }}>${(-trades.filter(t => t.exitReason === 'SKIPPED_BY_DEDUP').reduce((s, t) => s + (t.simProfit || 0), 0)).toFixed(0)}</span></span>}
                  {stats.exitReasons.skipped_risk > 0 && <span>Skip saved: <span style={{ color: (-trades.filter(t => t.exitReason === 'SKIPPED_BY_RISK').reduce((s, t) => s + (t.simProfit || 0), 0)) >= 0 ? '#16a34a' : '#dc2626' }}>${(-trades.filter(t => t.exitReason === 'SKIPPED_BY_RISK').reduce((s, t) => s + (t.simProfit || 0), 0)).toFixed(0)}</span></span>}
                </div>
              )}
              <div className="summary-details">
                {bySource.map(s => (
                  <span key={s.source}>{fmtSource(s.source)}: <span className={parseFloat(s.totalProfit) > 0 ? 'positive' : parseFloat(s.totalProfit) < 0 ? 'negative' : ''}>{s.winRate}% ({s.entered}, ${s.totalProfit})</span></span>
                ))}
              </div>
            </div>

            {/* Best / Worst */}
            <div className="best-worst-row">
              <div className="stats-card">
                <h4>Best Trades</h4>
                {bestTrades.map((t, i) => (
                  <div key={i} className="trade-highlight">
                    <span className="symbol">{t.symbol}</span>
                    <span className="trade-profit positive">+${t.profit?.toFixed(2)}</span>
                    <span className="detail">{fmtSource(t.source)}</span>
                  </div>
                ))}
                {bestTrades.length === 0 && <div style={{ fontSize: 12, color: '#999' }}>No winning trades</div>}
              </div>
              <div className="stats-card">
                <h4>Worst Trades</h4>
                {worstTrades.map((t, i) => (
                  <div key={i} className="trade-highlight">
                    <span className="symbol">{t.symbol}</span>
                    <span className="trade-profit negative">${t.profit?.toFixed(2)}</span>
                    <span className="detail">{fmtSource(t.source)}</span>
                  </div>
                ))}
                {worstTrades.length === 0 && <div style={{ fontSize: 12, color: '#999' }}>No losing trades</div>}
              </div>
            </div>

            {/* Filters */}
            <div className="trade-filters">
              <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}>
                <option value="all">Source: All</option>
                <option value={AI_SOURCE.CLAUDE}>Claude Only</option>
                <option value={AI_SOURCE.GPT}>GPT Only</option>
                <option value={AI_SOURCE.BOTH}>Both AIs</option>
              </select>
              <select value={filters.reason} onChange={e => setFilters(f => ({ ...f, reason: e.target.value }))}>
                <option value="all">Reason: All</option>
                <option value="target">Target</option>
                <option value="stop_loss">Stop Loss</option>
                <option value="time_cutoff">Time Cutoff</option>
                <option value="no_entry">No Entry</option>
                <option value="SKIPPED_BY_DEDUP">Dedup</option>
                <option value="SKIPPED_BY_RISK">Skip (Risk)</option>
              </select>
              <select value={filters.result} onChange={e => setFilters(f => ({ ...f, result: e.target.value }))}>
                <option value="all">Result: All</option>
                <option value="win">Wins</option>
                <option value="loss">Losses</option>
              </select>
            </div>

            {/* Trade Table */}
            <div className="stats-card" style={{ padding: '8px 12px' }}>
              <table className="trades-table">
                <thead>
                  <tr>
                    <SortHeader col="symbol" label="Symbol" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="source" label="Source" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="entry" label="Entry" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="exit" label="Exit" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="profit" label="P/L" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="reason" label="Reason" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="duration" label="Min" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                    <SortHeader col="scan" label="Scan" current={sortColumn} dir={sortDirection} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {pagedTrades.map((t, i) => (
                    <tr key={i}>
                      <td style={{ color: '#667eea', fontWeight: 600 }}>{t.symbol}</td>
                      <td>{fmtSource(t.source)}</td>
                      <td>{t.entryPrice ? `$${t.entryPrice.toFixed(2)}` : '-'}</td>
                      <td>{t.exitPrice ? `$${t.exitPrice.toFixed(2)}` : '-'}</td>
                      <td>
                        <span className={`trade-profit ${(t.profit || 0) > 0 ? 'positive' : (t.profit || 0) < 0 ? 'negative' : ''}`}>
                          {t.profit ? `$${t.profit.toFixed(2)}` : '-'}
                        </span>
                        {t.simProfit !== undefined && t.simProfit !== null && (t.exitReason === 'SKIPPED_BY_DEDUP' || t.exitReason === 'SKIPPED_BY_RISK') && (
                          <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>(sim: <span style={{ color: t.simProfit >= 0 ? '#16a34a' : '#dc2626' }}>${t.simProfit.toFixed(0)}</span>)</span>
                        )}
                      </td>
                      <td><span className={`exit-badge ${t.exitReason || ''}`}>{fmtReason(t.exitReason)}</span></td>
                      <td>{t.duration || '-'}</td>
                      <td style={{ fontSize: 11, color: '#888' }}>{fmtScanTime(t.scanTime, timeRange === 'today')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                  <span>Page {page + 1}/{totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next</button>
                </div>
              )}
            </div>
          </>
        )}
      </>
    );
  }
};

const fmtSource = (s) => s === AI_SOURCE.GPT ? 'GPT' : s === AI_SOURCE.CLAUDE ? 'Claude' : s === AI_SOURCE.BOTH ? 'Both' : s;
const fmtReason = (r) => r ? r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-';
const fmtScanTime = (t, timeOnly = false) => {
  if (!t) return '-';
  try {
    const d = new Date(t);
    const pad = (n) => String(n).padStart(2, '0');
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    if (timeOnly) return time;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${time}`;
  } catch { return '-'; }
};

const StatItem = ({ label, value, positive, negative }) => (
  <div className="stat-item">
    <span className="stat-label">{label}:</span>
    <span className={`stat-value ${positive ? 'positive' : ''} ${negative ? 'negative' : ''}`}>{value}</span>
  </div>
);

const SortHeader = ({ col, label, current, dir, onSort }) => (
  <th onClick={() => onSort(col)}>
    {label}
    {current === col && <span className="sort-arrow">{dir === 'asc' ? ' ▲' : ' ▼'}</span>}
  </th>
);

export default BacktestDialog;
